import { Injectable } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom, Observable, pairwise, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BocClient } from '../../shared/models/boc-client-transac';
import { FastLaneWizardState, FastLaneWizardStep } from '../models/fastlane-wizard-state';
import { FastLaneRequest, createDefaultPrototype } from '../models/fastlane.model';
import {
	SnackbarSubmissionFeedbackComponent
} from '../../shared/components/snackbar-submission-feedback/snackbar-submission-feedback.component';
import { TranslationService } from '../../shared/services/translation.service';
import { ErrorService } from '../../shared/services/error.service';

import { FastlaneAPIService } from './fastlane.api.service';
import { FastLaneService } from './fastlane.service';

export const PAGE_NAME_STEPS = ['mainData', 'itemsDetails', 'transportDetails', 'summary'];
interface ErrorResponse {
	code: string;
	message: string;
}
@Injectable({ providedIn: 'root' })
export class FastLaneWizardService {
	navigationCount = 0;
	previousUrl: string | undefined;
	urlData: string[] = [];
	backViewSubscription?: Subscription;
	prototypeRequestIdObjs: Observable<string>;
	shipToList?: BocClient[];
	private readonly prototypeRequestIdSubject = new BehaviorSubject(<string>'');
	private state: FastLaneWizardState;
	constructor(
		private readonly router: Router,
		private readonly fastLaneService: FastLaneService,
		private readonly fastlaneAPIService: FastlaneAPIService,

		private readonly snackBar: MatSnackBar,
		private readonly translationService: TranslationService,
		private readonly errorService: ErrorService
	) {
		const defaultPrototypeRequest = createDefaultPrototype();
		this.state = new FastLaneWizardState(defaultPrototypeRequest, defaultPrototypeRequest);
		this.prototypeRequestIdObjs = this.prototypeRequestIdSubject.asObservable();
	}

	actionToGoBack() {
		this.backViewSubscription = this.router.events
			.pipe(
				filter((ev): ev is RoutesRecognized => ev instanceof RoutesRecognized),
				pairwise()
			)
			.subscribe(ev => {
				for (const url of ev) {
					this.urlData.push(url.urlAfterRedirects);
				}
			});
		const disconIdx = this.urlData.indexOf('/fastlane/wizard/mainData');
		this.navigationCount = 1;
		return this.urlData[disconIdx - 1];
	}

	getNavigationCount() {
		return this.navigationCount;
	}

	actionCleanUrlData() {
		this.urlData = [];
	}

	actionSavePreviousPage(previousPage: string | undefined) {
		this.previousUrl = previousPage;
	}

	actionRetrievePreviousPage() {
		return this.previousUrl;
	}

	async checkWizardStateValidity(stepIndex: FastLaneWizardStep) {
		if (stepIndex > FastLaneWizardStep.MAINDATA) {
			this.state.stepIndex = stepIndex;
			if (this.state.prototypeRequest.form.getFormCustomerRequestName().value) {
				return true;
			}
			if (this.state.exiting) {
				this.state.exiting = false;
				return true;
			}
			await this.router.navigateByUrl('fastlane/wizard', { replaceUrl: true });
			const defaultPrototypeRequest = createDefaultPrototype();
			this.initPrototypeRequest(defaultPrototypeRequest, defaultPrototypeRequest);
			return false;
		}
		return true;
	}

	async actionMoveUsingSteps(step: FastLaneWizardStep) {
		if (this.state.stepIndex <= FastLaneWizardStep.MAINDATA) {
			return;
		}
		if (step < this.state.stepIndex) {
			this.navigationCount++;
			this.state.stepIndex = 0;
			const url = `fastlane/wizard/${ PAGE_NAME_STEPS[step] }`;
			await this.router.navigateByUrl(url);
		}
	}

	getCurrentRoute(): string {
		return this.router.url;
	}

	actionClearWizardHistory() {
		this.navigationCount = 1;
		this.state.isValid = false;
	}

	getState() {
		return this.state;
	}

	getStateFastLaneForm() {
		return this.state.prototypeRequest.form;
	}

	async actionGoNextStep() {
		const maxStepIndex = FastLaneWizardStep.SUMMARY;
		if (this.state.stepIndex >= maxStepIndex) {
			return;
		}
		this.state.stepIndex += 1;
		this.navigationCount++;
		const url = `fastlane/wizard/${ PAGE_NAME_STEPS[this.state.stepIndex] }`;
		await this.router.navigateByUrl(url);
	}

	actionGoPreviousStep() {
		return null;
	}

	actionExitWizard() {
		const defaultPrototypeRequest = createDefaultPrototype();
		this.initPrototypeRequest(defaultPrototypeRequest, defaultPrototypeRequest);
		this.fastLaneService.resetProdctFormGroup();
		this.navigationCount = 1;
		this.state.isValid = false;
		return true;
	}

	async actionSubmitOrder() {
		try {
			const requestObj = this.fastLaneService.requestOrderObj;
			if (requestObj.fastLaneItems) {
				for (const x of requestObj.fastLaneItems) {
					if (x.rddDate === 'Invalid date') {
						x.rddDate = '';
						x.rddDisplay = '';
					}
				}
			}

			const apiResponse = await firstValueFrom(this.fastlaneAPIService.submitOrder(requestObj));
			const requestId = apiResponse.id;
			this.fastLaneService.resetProdctFormGroup();
			await this.router.navigateByUrl('/home');
			this.snackBar.openFromComponent(
				SnackbarSubmissionFeedbackComponent,
				{
					data: {
						hideResponseDetails: true,
						isThanksMessage: true,
						isRedirectLink: false,
						submitThanksMessage: this.translationService.getTR('confirm.thanks.fastlane').concat(' ')
							.concat(`(${ String(requestId) })`),
					},
					duration: 20_000,
					panelClass: ['copo-snackbar-submission-feedback-container']
				}
			);
			return true;
		} catch (err) {
			if (!(err instanceof HttpErrorResponse)) {
				return false;
			}
			if (typeof err.error === 'object' && err.error !== null && (<{ message: string }>err.error).message) {
				if (err.status === 401) {
					this.errorService.setErrorMsg(
						this.translationService.getTR('fastlane.submit.error.msg.notoken'),
						'fastlane.submit.request.error.msg.title'
					);
				} else if ((<ErrorResponse>err.error).code === 'MANDATORYERROR') {
					this.errorService.setErrorMsg(
						this.translationService
							.getTR('fastlane.submit.error.msg.mandatoryfield'),
						'fastlane.submit.request.error.msg.title'
					);
				} else if ((<ErrorResponse>err.error).code === 'BAD_DATA'
					|| (<ErrorResponse>err.error).code === 'service_needed') {
					this.errorService.setErrorMsg(
						this.translationService.getTR('fastlane.submit.error.msg.nosoldtoshipto'),
						'fastlane.submit.request.error.msg.title'
					);
				} else {
					this.errorService.setErrorMsg(
						(<{ message: string }>err.error).message,
						'fastlane.submit.request.error.msg.title'
					);
				}
			} else {
				this.errorService.setErrorMsg(
					this.translationService.getTR('fastlane.submit.error.msg'),
					'fastlane.submit.request.error.msg.title'
				);
				console.error(err);
			}
			return false;
		}
	}

	private initPrototypeRequest(prototypeRequest: FastLaneRequest, lastSavedPrototypeRequest: FastLaneRequest) {
		this.state = new FastLaneWizardState(prototypeRequest, lastSavedPrototypeRequest);
		this.state.isValid = true;
	}
}
